import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useActivePopup } from "@/hooks/useActivePopup";

const STORAGE_KEY = "deepc_popup_dismissed_at";
const SUPPRESS_DAYS = 1;

/**
 * Subtle exit-intent popup. Triggers when the cursor moves toward the top of
 * the viewport after a short dwell. Suppressed on mobile, in admin,
 * for 1 day after dismissal, and never appears more than once per session.
 */
export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const { data: popup } = useActivePopup();
  const [open, setOpen] = useState(false);
  const [shownThisSession, setShownThisSession] = useState(false);

  useEffect(() => {
    if (!popup) return;
    if (pathname.startsWith("/admin")) return;
    if (shownThisSession) return;

    // Suppress on touch / small viewports — exit-intent isn't meaningful there.
    const isTouch = window.matchMedia("(hover: none)").matches || window.innerWidth < 768;
    if (isTouch) return;

    // Recently dismissed?
    const lastDismissed = Number(localStorage.getItem(STORAGE_KEY) ?? 0);
    if (Date.now() - lastDismissed < SUPPRESS_DAYS * 24 * 60 * 60 * 1000) return;

    let activated = false;
    let armed = false;
    const showPopup = () => {
      if (!armed || activated) return;
      activated = true;
      setOpen(true);
      setShownThisSession(true);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (e.clientY <= 32) showPopup();
    };

    const onMouseOut = (e: MouseEvent) => {
      // Cursor left the viewport through the browser chrome/top edge.
      if (e.relatedTarget || (e as any).toElement) return;
      if (e.clientY <= 48) showPopup();
    };

    // Small dwell: wait 8s before arming so it never fires immediately.
    const armT = setTimeout(() => {
      armed = true;
    }, 8000);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(armT);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [popup, pathname, shownThisSession]);

  if (!popup || !open) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
  };

  const isExternal = popup.cta_url && /^https?:\/\//.test(popup.cta_url);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-popup-title"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md bg-card text-card-foreground border border-border rounded-lg shadow-card overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 inline-flex items-center justify-center size-8 rounded-md text-foreground/55 hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="size-4" />
        </button>

        {popup.image_url && (
          <img src={popup.image_url} alt="" className="w-full aspect-[16/8] object-cover" />
        )}

        <div className="p-6 md:p-8">
          <p className="eyebrow">Before you go</p>
          <h2 id="exit-popup-title" className="display mt-3 text-2xl text-foreground leading-snug">
            {popup.headline}
          </h2>
          {popup.body && <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{popup.body}</p>}

          {popup.cta_label && popup.cta_url && (
            <div className="mt-6 flex items-center gap-4">
              {isExternal ? (
                <a
                  href={popup.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  onClick={dismiss}
                >
                  {popup.cta_label}
                </a>
              ) : (
                <Link
                  to={popup.cta_url}
                  className="inline-flex items-center h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  onClick={dismiss}
                >
                  {popup.cta_label}
                </Link>
              )}
              <button
                type="button"
                onClick={dismiss}
                className="text-xs text-foreground/55 hover:text-foreground transition-colors"
              >
                No thanks
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
